import { scoreFields, getRequiredFields } from '../validation/scoresSchema';

/**
 * Validation Utilities for Score Forms
 * Provides helper functions for validating scores and tracking form completion
 */

/**
 * Validate a single score value
 * @param {any} value - Score value to validate
 * @param {number} maxScore - Maximum allowed score (default: 100)
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateScore = (value, maxScore = 100) => {
  // Handle empty values
  if (value === null || value === undefined || value === '') {
    return { isValid: false, error: 'This field is required' };
  }

  // Convert to number
  const numValue = Number(value);

  // Check if it's a valid number
  if (isNaN(numValue)) {
    return { isValid: false, error: 'Please enter a valid number' };
  }

  // Check range
  if (numValue < 0) {
    return { isValid: false, error: 'Score must be at least 0' };
  }

  if (numValue > maxScore) {
    return { isValid: false, error: `Score must not exceed ${maxScore}` };
  }

  // Check decimal places
  const decimalPlaces = (value.toString().split('.')[1] || '').length;
  if (decimalPlaces > 2) {
    return { isValid: false, error: 'Score must have at most 2 decimal places' };
  }

  return { isValid: true, error: null };
};

/**
 * Validate optional score value
 * @param {any} value - Score value to validate
 * @param {number} maxScore - Maximum allowed score (default: 100)
 * @returns {{isValid: boolean, error: string|null}}
 */
export const validateOptionalScore = (value, maxScore = 100) => {
  // Optional fields can be empty
  if (value === null || value === undefined || value === '') {
    return { isValid: true, error: null };
  }

  // If value is provided, validate it
  return validateScore(value, maxScore);
};

/**
 * Validate entire scores object
 * @param {Object} scores - Object containing all score fields
 * @returns {{isValid: boolean, errors: Object}}
 */
export const validateScoreObject = (scores) => {
  const errors = {};
  let isValid = true;

  scoreFields.forEach((field) => {
    const value = scores[field.key];
    const validation = field.required
      ? validateScore(value)
      : validateOptionalScore(value);

    if (!validation.isValid) {
      errors[field.key] = validation.error;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/**
 * Get validation errors for all fields
 * @param {Object} scores - Object containing all score fields
 * @returns {Object} Object with field names as keys and error messages as values
 */
export const getValidationErrors = (scores) => {
  const { errors } = validateScoreObject(scores);
  return errors;
};

/**
 * Check if form is valid (all required fields filled and valid)
 * @param {Object} scores - Object containing all score fields
 * @returns {boolean}
 */
export const isFormValid = (scores) => {
  const { isValid } = validateScoreObject(scores);
  return isValid;
};

/**
 * Get completion percentage of form
 * @param {Object} scores - Object containing all score fields
 * @returns {number} Percentage (0-100)
 */
export const getCompletionPercentage = (scores) => {
  if (!scores) return 0;

  const totalFields = scoreFields.length;
  let completedFields = 0;

  scoreFields.forEach((field) => {
    const value = scores[field.key];
    const validation = field.required
      ? validateScore(value)
      : validateOptionalScore(value);

    // Count as completed if value exists and is valid
    if (value !== null && value !== undefined && value !== '' && validation.isValid) {
      completedFields++;
    }
  });

  return Math.round((completedFields / totalFields) * 100);
};

/**
 * Get required fields completion percentage
 * @param {Object} scores - Object containing all score fields
 * @returns {number} Percentage (0-100)
 */
export const getRequiredFieldsCompletion = (scores) => {
  if (!scores) return 0;

  const requiredFields = scoreFields.filter(field => field.required);
  const totalRequired = requiredFields.length;
  let completedRequired = 0;

  requiredFields.forEach((field) => {
    const value = scores[field.key];
    const validation = validateScore(value);

    if (validation.isValid) {
      completedRequired++;
    }
  });

  return Math.round((completedRequired / totalRequired) * 100);
};

/**
 * Get list of incomplete required fields
 * @param {Object} scores - Object containing all score fields
 * @returns {Array<{key: string, label: string}>}
 */
export const getIncompleteRequiredFields = (scores) => {
  const requiredFields = scoreFields.filter(field => field.required);
  const incomplete = [];

  requiredFields.forEach((field) => {
    const value = scores[field.key];
    const validation = validateScore(value);

    if (!validation.isValid) {
      incomplete.push({
        key: field.key,
        label: field.label,
        error: validation.error,
      });
    }
  });

  return incomplete;
};

/**
 * Get list of fields with errors
 * @param {Object} scores - Object containing all score fields
 * @returns {Array<{key: string, label: string, error: string}>}
 */
export const getFieldsWithErrors = (scores) => {
  const fieldsWithErrors = [];

  scoreFields.forEach((field) => {
    const value = scores[field.key];
    const validation = field.required
      ? validateScore(value)
      : validateOptionalScore(value);

    if (!validation.isValid && validation.error) {
      fieldsWithErrors.push({
        key: field.key,
        label: field.label,
        error: validation.error,
      });
    }
  });

  return fieldsWithErrors;
};

/**
 * Check if scores object has been modified
 * @param {Object} originalScores - Original scores object
 * @param {Object} currentScores - Current scores object
 * @returns {boolean}
 */
export const hasUnsavedChanges = (originalScores, currentScores) => {
  if (!originalScores && !currentScores) return false;
  if (!originalScores || !currentScores) return true;

  return scoreFields.some((field) => {
    const originalValue = originalScores[field.key];
    const currentValue = currentScores[field.key];

    // Convert to numbers for comparison, treating empty/null as 0
    const original = originalValue === null || originalValue === undefined || originalValue === ''
      ? null
      : Number(originalValue);
    const current = currentValue === null || currentValue === undefined || currentValue === ''
      ? null
      : Number(currentValue);

    return original !== current;
  });
};

/**
 * Calculate total score
 * @param {Object} scores - Object containing all score fields
 * @returns {number} Total score
 */
export const calculateTotalScore = (scores) => {
  if (!scores) return 0;

  let total = 0;

  scoreFields.forEach((field) => {
    const value = scores[field.key];
    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        total += numValue;
      }
    }
  });

  return Math.round(total * 100) / 100; // Round to 2 decimal places
};

/**
 * Calculate average score
 * @param {Object} scores - Object containing all score fields
 * @returns {number} Average score
 */
export const calculateAverageScore = (scores) => {
  if (!scores) return 0;

  let total = 0;
  let count = 0;

  scoreFields.forEach((field) => {
    const value = scores[field.key];
    if (value !== null && value !== undefined && value !== '') {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        total += numValue;
        count++;
      }
    }
  });

  if (count === 0) return 0;
  return Math.round((total / count) * 100) / 100; // Round to 2 decimal places
};

/**
 * Sanitize score value for submission
 * @param {any} value - Score value
 * @returns {number|null}
 */
export const sanitizeScoreValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return null;
  }

  // Round to 2 decimal places
  return Math.round(numValue * 100) / 100;
};

/**
 * Sanitize scores object for submission
 * @param {Object} scores - Scores object
 * @returns {Object} Sanitized scores object
 */
export const sanitizeScoresObject = (scores) => {
  const sanitized = {};

  scoreFields.forEach((field) => {
    sanitized[field.key] = sanitizeScoreValue(scores[field.key]);
  });

  return sanitized;
};

export default {
  validateScore,
  validateOptionalScore,
  validateScoreObject,
  getValidationErrors,
  isFormValid,
  getCompletionPercentage,
  getRequiredFieldsCompletion,
  getIncompleteRequiredFields,
  getFieldsWithErrors,
  hasUnsavedChanges,
  calculateTotalScore,
  calculateAverageScore,
  sanitizeScoreValue,
  sanitizeScoresObject,
};